// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import ActionColumns from '~/components/Modules/ActionColumns';
import { ConstructFragmentOverride, GetCopyFromJson } from '~/contentstack/rendering';
import SvgImage from '../components/images/SvgImage';
import Overview from '../components/Overview';
import RedBar from '../components/RedBar';

import './BenefitsPerks.scss';

export const query = graphql`
	fragment BenefitsPerksFragment on Ozk_pageContent {
		benefits_perks {
			heading
			subheading
			include_red_line

			benefits {
				icon {
					...AssetFragment
				}
				heading
				copy {
					json
				}
			}
		}
	}
`;

/**
 * @typedef Benefit
 * @property {import('../CSTypes').CSFile} icon
 * @property {string} heading
 * @property {import('../CSTypes').CSJRTE} copy
 *
 * @typedef BenefitsPerksOptions
 * @property {string} heading The heading of the block.
 * @property {string} subheading The subheading of the block.
 * @property {boolean} include_red_line A value indicating whether this section should be headed by a red line.
 * @property {Benefit[]} benefits The list of benefits and perks to show.
 *
 * @typedef BenefitsPerksProps
 * @property {import('~/contentstack/types').ModelWithLivePreviewAttr<BenefitsPerksOptions>} [options]
 */
/** @type {import('~/types/React').ReactFC<BenefitsPerksProps>} */
const BenefitsPerks = ({ options, ...rest }) => {
	if (!options) {
		return null;
	}
	const { benefits, heading, subheading, include_red_line = true } = options;

	return (
		<Overview {...rest} className={classNames(rest.className, 'benefits-and-perks')} alignment="center">
			{include_red_line && <RedBar />}
			<h2 className="heading" {...options.$?.heading}>
				{heading}
			</h2>
			<p className="subheading" {...options.$?.subheading}>
				{subheading}
			</p>

			<ActionColumns className="perk-row">
				{benefits.map((perk, j) => {
					const copy = GetCopyFromJson(perk.copy, ConstructFragmentOverride('p'));

					return (
						<div className="single-perk col-md-3" key={j}>
							<SvgImage options={{ alt_text: perk.icon.title, svg: perk.icon }} />
							<h4 className="heading">{perk.heading}</h4>
							<p className="copy">{copy}</p>
						</div>
					);
				})}
			</ActionColumns>
		</Overview>
	);
};

export default BenefitsPerks;
